import styled from "@emotion/styled";
import { Paper, Theme } from "@mui/material";
import Box, { BoxProps } from "@mui/material/Box";
import Modal, { ModalProps } from "@mui/material/Modal";
import { ResponsiveStyleValue } from "@mui/system";
import React, { ReactNode, FC, useState } from "react";
import { Loader } from "../Loader";
import ModalBody from "./ModalBody";
import ModalFooter from "./ModalFooter";
import ModalHeader from "./ModalHeader";

export interface IModalComponentProps
  extends Omit<ModalProps, "children" | "title"> {
  boxProps?: BoxProps;
  modalFooter?: ReactNode;
  width?:
    | number
    | string
    | ((theme: Theme) => ResponsiveStyleValue<string | number>);
  small?: boolean;
  onClose: () => void;
  children?: ReactNode;
  closeOnBackdropClick?: boolean;
  disableBodyPadding?: boolean;
  isLoading?: boolean;
  initialBodyShadow?: boolean;
  title?: ReactNode;
}

const StyledBox = styled(Box)(`max-height: 100vh; max-height: 100svh`);

export const ModalComponent: FC<IModalComponentProps> = ({
  modalFooter,
  small,
  width,
  onClose,
  title,
  children,
  boxProps,
  closeOnBackdropClick,
  disableBodyPadding,
  isLoading,
  initialBodyShadow,
  ...modalProps
}) => {
  const modalWidth = width ? width : small ? 375 : 768;
  const [hasScroll, setHasScroll] = useState(initialBodyShadow || false);

  return (
    <Modal
      {...modalProps}
      onClose={(e: React.SyntheticEvent, reason) => {
        e.stopPropagation();
        if (reason === "backdropClick" && !closeOnBackdropClick) {
          return;
        }
        onClose();
      }}
      sx={{
        ...modalProps.sx,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
      }}
    >
      <Paper
        elevation={0}
        component={Box}
        width={modalWidth}
        margin="0 auto"
        maxWidth="100%"
        borderRadius={{ xs: 0, sm: 2 }}
        onClick={(e) => e.stopPropagation()}
        position="relative"
      >
        {isLoading && <Loader position="absolute" blur />}
        <StyledBox
          {...boxProps}
          sx={{
            ...boxProps?.sx,
            pb: !modalFooter && !hasScroll ? 6 : 0,
            maxHeight: { md: "90vh" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ModalHeader
            hasShadow={hasScroll}
            title={title}
            onCrossClick={onClose}
          />
          <ModalBody
            hasScroll={hasScroll}
            setHasScroll={setHasScroll}
            disableBodyPadding={disableBodyPadding}
          >
            {children}
          </ModalBody>
          {!!modalFooter && (
            <ModalFooter hasShadow={hasScroll}>{modalFooter}</ModalFooter>
          )}
        </StyledBox>
      </Paper>
    </Modal>
  );
};
